import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  HMSRoomProvider,
  useHMSActions,
  useHMSStore,
  useHMSNotifications,
  useVideo,
  selectPeers,
  selectIsLocalAudioEnabled,
  selectIsLocalVideoEnabled,
  selectRoomState,
  selectRecordingState,
  HMSRoomState,
  HMSNotificationTypes,
} from "@100mslive/react-sdk";
import { Mic, MicOff, Video, VideoOff, PhoneOff, Loader2, ShieldCheck, Circle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { rtcApi } from "@/lib/api";
import { useAuthStore } from "@/store/authStore";
import { toast } from "sonner";

const TEAL = "#0EA5A6";

function PeerTile({ peer, mirror, className, style }) {
  const { videoRef } = useVideo({ trackId: peer?.videoTrack });
  return (
    <div className={className} style={{ background: "#0b1220", overflow: "hidden", ...style }}>
      {peer?.videoTrack ? (
        <video
          ref={videoRef}
          autoPlay
          muted={peer?.isLocal}
          playsInline
          style={{ width: "100%", height: "100%", objectFit: "cover", transform: mirror ? "scaleX(-1)" : "none" }}
        />
      ) : (
        <div className="w-full h-full flex flex-col items-center justify-center text-white/70">
          <div className="h-16 w-16 rounded-full bg-white/10 flex items-center justify-center text-2xl font-semibold">
            {(peer?.name || "?").slice(0, 1).toUpperCase()}
          </div>
          <p className="mt-2 text-sm">{peer?.name || "Participant"}</p>
        </div>
      )}
    </div>
  );
}

function CallRoom({ bookingId, meta, onExit }) {
  const actions = useHMSActions();
  const peers = useHMSStore(selectPeers);
  const audioOn = useHMSStore(selectIsLocalAudioEnabled);
  const videoOn = useHMSStore(selectIsLocalVideoEnabled);
  const roomState = useHMSStore(selectRoomState);
  const recording = useHMSStore(selectRecordingState);

  const local = peers.find((p) => p.isLocal);
  const remote = peers.find((p) => !p.isLocal);
  const recordingOn = Boolean(recording?.server?.running || recording?.browser?.running);

  const leave = useCallback(async () => {
    try {
      await actions.leave();
    } catch (e) {
      /* ignore */
    }
    onExit();
  }, [actions, onExit]);

  useEffect(() => {
    return () => {
      actions.leave().catch(() => {});
    };
  }, [actions]);

  return (
    <div className="fixed inset-0 bg-[#070b14] flex flex-col" data-testid="video-call-room">
      {/* Top bar */}
      <div className="flex items-center justify-between px-4 py-3 bg-black/40">
        <div className="min-w-0">
          <p className="text-white font-semibold truncate">
            {meta?.role === "doctor" ? meta?.patientName : `Dr. ${meta?.doctorName || "Consultation"}`}
          </p>
          <p className="text-xs text-white/60 truncate">
            {roomState === HMSRoomState.Connected
              ? remote
                ? "Connected"
                : "Waiting for the other person to join…"
              : roomState === HMSRoomState.Reconnecting
              ? "Reconnecting…"
              : "Connecting…"}
          </p>
        </div>
        {recordingOn && (
          <span
            className="flex items-center gap-1.5 text-xs font-semibold text-white px-2.5 py-1 rounded-full"
            style={{ background: "#DC262633" }}
            data-testid="recording-indicator"
          >
            <Circle className="h-2.5 w-2.5 fill-red-500 text-red-500 animate-pulse" /> Recording
          </span>
        )}
      </div>

      {/* Video area */}
      <div className="relative flex-1">
        {remote ? (
          <PeerTile peer={remote} className="absolute inset-0" />
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-white/70">
            <Loader2 className="h-8 w-8 animate-spin" />
            <p className="mt-3 text-sm">Waiting for {meta?.peerRole || "the other participant"} to join…</p>
          </div>
        )}
        {local && (
          <PeerTile
            peer={local}
            mirror
            className="absolute rounded-xl border-2 border-white/70 shadow-lg"
            style={{ bottom: 100, right: 16, width: 120, height: 160 }}
          />
        )}
      </div>

      {/* Controls */}
      <div className="flex items-center justify-center gap-4 py-5 bg-black/40">
        <button
          onClick={() => actions.setLocalAudioEnabled(!audioOn)}
          className="h-14 w-14 rounded-full flex items-center justify-center text-white"
          style={{ background: audioOn ? "#ffffff22" : "#64748b" }}
          data-testid="toggle-mic-btn"
          aria-label={audioOn ? "Mute" : "Unmute"}
        >
          {audioOn ? <Mic className="h-6 w-6" /> : <MicOff className="h-6 w-6" />}
        </button>
        <button
          onClick={() => actions.setLocalVideoEnabled(!videoOn)}
          className="h-14 w-14 rounded-full flex items-center justify-center text-white"
          style={{ background: videoOn ? "#ffffff22" : "#64748b" }}
          data-testid="toggle-camera-btn"
          aria-label={videoOn ? "Camera off" : "Camera on"}
        >
          {videoOn ? <Video className="h-6 w-6" /> : <VideoOff className="h-6 w-6" />}
        </button>
        <button
          onClick={leave}
          className="h-14 w-14 rounded-full flex items-center justify-center text-white bg-red-600"
          data-testid="end-call-btn"
          aria-label="End call"
        >
          <PhoneOff className="h-6 w-6" />
        </button>
      </div>
    </div>
  );
}

function VideoConsultInner() {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const actions = useHMSActions();
  const errorNotif = useHMSNotifications(HMSNotificationTypes.ERROR);

  const [phase, setPhase] = useState("consent"); // consent | joining | in-call | error | unavailable
  const [meta, setMeta] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (errorNotif) {
      const msg = errorNotif.data?.message || errorNotif.data?.description || "Video connection failed.";
      setError(msg);
      setPhase("error");
      actions.leave().catch(() => {});
    }
  }, [errorNotif, actions]);

  useEffect(() => {
    rtcApi
      .config()
      .then((r) => {
        if (!r.data?.configured) setPhase("unavailable");
      })
      .catch(() => {});
  }, []);

  const startCall = useCallback(async () => {
    if (!user?._id) {
      setError("Please sign in to join the consultation.");
      setPhase("error");
      return;
    }
    setPhase("joining");
    try {
      const { data } = await rtcApi.join(bookingId, user._id);
      setMeta(data);
      await actions.join({
        userName: data.role === "doctor" ? `Dr. ${data.doctorName || "Doctor"}` : data.patientName || "Patient",
        authToken: data.token,
        settings: { isAudioMuted: false, isVideoMuted: false },
      });
      setPhase("in-call");
    } catch (e) {
      const status = e?.response?.status;
      if (status === 503) {
        setPhase("unavailable");
      } else {
        setError(e?.response?.data?.detail || "Could not connect to the consultation.");
        setPhase("error");
        toast.error("Failed to join the call");
      }
    }
  }, [actions, bookingId, user]);

  const exit = useCallback(() => navigate("/consultations"), [navigate]);

  if (phase === "unavailable") {
    return (
      <Centered testid="video-unavailable">
        <ShieldCheck className="h-10 w-10" style={{ color: TEAL }} />
        <p className="mt-3 font-semibold">Video consultation is being set up</p>
        <p className="text-sm text-muted-foreground mt-1 max-w-sm">
          Secure video calling isn&apos;t activated on this account yet. Please try again shortly.
        </p>
        <Button className="mt-5 rounded-2xl" onClick={exit} data-testid="video-unavailable-back">
          Back to consultations
        </Button>
      </Centered>
    );
  }

  if (phase === "error") {
    return (
      <Centered testid="video-error">
        <PhoneOff className="h-10 w-10 text-red-500" />
        <p className="mt-3 font-semibold">Unable to join</p>
        <p className="text-sm text-muted-foreground mt-1 max-w-sm">{error}</p>
        <Button className="mt-5 rounded-2xl" onClick={exit}>
          Back
        </Button>
      </Centered>
    );
  }

  if (phase === "declined") {
    return (
      <Centered testid="video-declined">
        <Video className="h-10 w-10" style={{ color: TEAL }} />
        <p className="mt-3 font-semibold">Recording is required for video consults</p>
        <p className="text-sm text-muted-foreground mt-1 max-w-sm">
          Video consultations can only take place with recording enabled, for your medical record and safety. If
          you&apos;d prefer not to be recorded, please book an in-person visit instead.
        </p>
        <div className="flex gap-3 mt-5">
          <Button variant="outline" className="rounded-2xl" onClick={exit} data-testid="video-declined-back">
            Go back
          </Button>
          <Button
            className="rounded-2xl text-white"
            style={{ background: TEAL }}
            onClick={() => navigate("/doctors")}
            data-testid="book-in-person"
          >
            Book in-person visit
          </Button>
        </div>
      </Centered>
    );
  }

  if (phase === "joining") {
    return (
      <Centered testid="video-joining">
        <Loader2 className="h-8 w-8 animate-spin" style={{ color: TEAL }} />
        <p className="mt-3 text-sm text-muted-foreground">Connecting you securely…</p>
      </Centered>
    );
  }

  if (phase === "in-call") {
    return <CallRoom bookingId={bookingId} meta={meta} onExit={exit} />;
  }

  // consent gate
  return (
    <Centered testid="video-consent">
      <div className="h-14 w-14 rounded-2xl flex items-center justify-center" style={{ background: TEAL + "1A" }}>
        <Video className="h-7 w-7" style={{ color: TEAL }} />
      </div>
      <p className="mt-4 text-lg font-semibold">Join your video consultation</p>
      <div className="mt-3 rounded-2xl border border-border p-4 text-sm text-muted-foreground max-w-md text-left">
        <p className="flex items-center gap-2 text-foreground font-semibold">
          <Circle className="h-2.5 w-2.5 fill-red-500 text-red-500 shrink-0" /> This call will be recorded
        </p>
        <p className="mt-2">We record your consultation to:</p>
        <ul className="mt-1 space-y-1 list-disc pl-5">
          <li>keep an accurate medical record so your doctor and future care stay informed;</li>
          <li>support your safety, care quality and medical audit / compliance (India DPDP).</li>
        </ul>
        <p className="mt-2">
          It&apos;s encrypted, stored securely, and accessible only to authorised medical staff involved in your care.
        </p>
        <p className="mt-2">
          By tapping <b className="text-foreground">Agree &amp; Join</b> you consent to camera &amp; microphone access and
          to this recording.
        </p>
      </div>
      <div className="flex gap-3 mt-5">
        <Button variant="outline" className="rounded-2xl" onClick={() => setPhase("declined")} data-testid="video-decline">
          Decline
        </Button>
        <Button className="rounded-2xl text-white" style={{ background: TEAL }} onClick={startCall} data-testid="video-agree">
          Agree &amp; Join
        </Button>
      </div>
    </Centered>
  );
}

function Centered({ children, testid }) {
  return (
    <div className="min-h-[70vh] flex flex-col items-center justify-center text-center px-6" data-testid={testid}>
      {children}
    </div>
  );
}

export default function VideoConsult() {
  return (
    <HMSRoomProvider>
      <VideoConsultInner />
    </HMSRoomProvider>
  );
}
